// @flow
import React, { useEffect } from "react";
import { Button, Alert } from "react-bootstrap";
import { Link, Redirect, useHistory, useLocation } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

// actions
import { resetAuth, loginUser } from "../../redux/actions";

import { useQuery } from "../../hooks";

// components
import { VerticalForm, FormInput } from "../../components";
import Spinner from "../../components/Spinner";

import AccountLayout from "./AccountLayout";

// Google Analytics
import { getAnalytics, logEvent } from "firebase/analytics";

/* bottom link */
const BottomLink = () => {
  const { t } = useTranslation();

  return (
    <footer className="footer footer-alt">
      <p className="text-muted">
        {t("Don't have an account?")}{" "}
        {/* TODO: comment out for supplier connection, change to /register/customer */}
        <Link to={"/register"} className="text-muted ms-1">
          <b>{t("Sign Up")}</b>
        </Link>
      </p>
    </footer>
  );
};

const Login = () => {
  const analytics = getAnalytics();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  // used for redirect when user clicks link in email
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode");
  const email = queryParams.get("email");
  const code = queryParams.get("code");

  // used for redirect when user is logged in
  const query = useQuery();
  const next = query.get("next");

  if (mode !== null && mode !== "") {
    let path = "/";
    // Handle the user management action.
    switch (mode) {
      case "resetPassword":
        // Display reset password handler and UI.
        path = "/new-password";
        break;
      case "verifyEmail":
        // Display email verification handler and UI.
        path = "/verify-email";
        break;
      default:
      // Error: invalid mode.
    }
    history.push({
      pathname: path,
      search: `?mode=${mode}&email=${email}&code=${code}`,
    });
  }

  useEffect(() => {
    document.title = `${t("Login")} - Line Up`;
    logEvent(analytics, "screen_view", {
      firebase_screen: "Login",
    });
  }, [t]);

  useEffect(() => {
    dispatch(resetAuth());
  }, [dispatch]);

  const { loading, userLoggedIn, user, error } = useSelector((state) => ({
    loading: state.Auth.loading,
    user: state.Auth.user,
    error: state.Auth.error,
    userLoggedIn: state.Auth.userLoggedIn,
  }));
  /*
   * form validation schema
   */
  const schemaResolver = yupResolver(
    yup.object().shape({
      username: yup.string().required(t("Please enter Username")),
      password: yup.string().required(t("Please enter Password")),
    })
  );

  /*
   * handle form submission
   */
  const onSubmit = (formData) => {
    dispatch(loginUser(formData["username"], formData["password"]));
  };

  return (
    <>
      {console.log("LOGGED IN: ", userLoggedIn, user)}
      {userLoggedIn || user ? (
        <Redirect to={next ? next : "/"}></Redirect>
      ) : null}

      <AccountLayout bottomLinks={<BottomLink />}>
        <h4 className="mt-0">{t("Sign In")}</h4>
        <p className="text-muted mb-4">
          {t("Enter your email address and password to access account.")}
        </p>
        {error && error.message && (
          <Alert
            variant={error.statusCode === 999 ? "warning" : "danger"}
            className="my-2"
          >
            {t(error.message)}
          </Alert>
        )}

        <VerticalForm onSubmit={onSubmit} resolver={schemaResolver}>
          <FormInput
            label={t("E-Mail")}
            type="text"
            name="username"
            placeholder={t("Enter your E-Mail")}
            containerClass={"mb-3"}
          />
          <FormInput
            label={t("Password")}
            type="password"
            name="password"
            placeholder={t("Enter your password")}
            containerClass={"mb-3"}
          >
            <Link to="/forget-password" className="text-muted float-end">
              <small>{t("Forgot your password?")}</small>
            </Link>
          </FormInput>

          <div className="d-grid mb-0 text-center">
            <Button variant="primary" type="submit" disabled={loading}>
              {!loading && <div>Login</div>}
              {loading && (
                <Spinner
                  className="spinner-border-sm"
                  tag="span"
                  color="white"
                />
              )}
            </Button>
          </div>
        </VerticalForm>
      </AccountLayout>
    </>
  );
};

export default Login;
